import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  inTimeframe, TIMEFRAMES, fullName, time, shortDate,
} from '../lib/data';
import { useWorklist, useProviders, useLocations } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import RiskBadge from '../components/RiskBadge';
import StatusPill from '../components/StatusPill';
import EmptyState from '../components/EmptyState';
import PeekDrawer from '../components/PeekDrawer';
import AppointmentCalendar from '../components/AppointmentCalendar';
import AddAppointment from '../components/AddAppointment';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDays, Search, Download, ChevronRight, Layers, List, CalendarPlus } from 'lucide-react';
import { toast } from '../lib/aiToast';

const VISIT_TYPES = ['New patient', 'Follow-up', 'Procedure', 'Surgery'];
const TYPE_CHIP = { 'New patient': 'pill-indigo', 'Follow-up': 'pill-slate', Procedure: 'pill-violet', Surgery: 'pill-amber' };
const TYPE_ORDER = { 'New patient': 0, Procedure: 1, Surgery: 2, 'Follow-up': 3 };

// Date buckets relative to the demo "today" (reuses inTimeframe's NOW_DATE).
const DATE_ORDER = { Today: 0, 'Next 30 days': 1, 'Past 30 days': 2, 'Earlier / later': 3 };
const dateBucket = (t) =>
  inTimeframe(t, 'today') ? 'Today'
    : inTimeframe(t, 'next30') ? 'Next 30 days'
      : inTimeframe(t, 'past30') ? 'Past 30 days'
        : 'Earlier / later';

export default function Appointments() {
  const { session } = useAuth();
  const nav = useNavigate();
  const practiceId = session?.practice?.id || 'prac_001';
  const scopeAll = practiceId === 'all';

  const [q, setQ] = useState('');
  const [timeframe, setTimeframe] = useState('all');
  const [provider, setProvider] = useState('all');
  const [location, setLocation] = useState('all');
  const [payer, setPayer] = useState('all');
  const [visitType, setVisitType] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'type' | 'date'
  const [viewMode, setViewMode] = useState('list');
  const [peek, setPeek] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const { data: base = [] } = useWorklist(practiceId);
  const { data: providers = [] } = useProviders(practiceId);
  const { data: locations = [] } = useLocations(practiceId);
  const payers = useMemo(() => Array.from(new Map(base.map((w) => [w.payer?.id, w.payer]).filter(([id]) => id)).values()), [base]);

  const rows = useMemo(() => base
    .filter((w) => inTimeframe(w.apt.startTime, timeframe))
    .filter((w) => provider === 'all' || w.apt.providerId === provider)
    .filter((w) => location === 'all' || w.apt.locationId === location)
    .filter((w) => payer === 'all' || w.payer?.id === payer)
    .filter((w) => visitType === 'all' || w.visitType === visitType)
    .filter((w) => !q || `${fullName(w.patient)} ${w.plan?.planName} ${w.apt.cpt}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => new Date(a.apt.startTime) - new Date(b.apt.startTime)),
    [base, timeframe, provider, location, payer, visitType, q]);

  // Grouping reorders rows so each group renders contiguously (still by time within a group).
  const view = useMemo(() => {
    if (groupBy === 'type') return [...rows].sort((a, b) => (TYPE_ORDER[a.visitType] ?? 9) - (TYPE_ORDER[b.visitType] ?? 9));
    if (groupBy === 'date') return [...rows].sort((a, b) => (DATE_ORDER[dateBucket(a.apt.startTime)] ?? 9) - (DATE_ORDER[dateBucket(b.apt.startTime)] ?? 9) || new Date(a.apt.startTime) - new Date(b.apt.startTime));
    return rows;
  }, [rows, groupBy]);
  const colSpan = 10 + (scopeAll ? 1 : 0);

  const detailHref = (w) => `/app/appointments/${w.apt.id}`;
  const filtersActive = timeframe !== 'all' || provider !== 'all' || location !== 'all' || payer !== 'all' || visitType !== 'all' || q;
  const clear = () => { setTimeframe('all'); setProvider('all'); setLocation('all'); setPayer('all'); setVisitType('all'); setQ(''); };
  const selCls = 'h-9 w-auto min-w-[120px] bg-white text-sm';

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Schedule" title="Appointments" subtitle="Every scheduled appointment in one place — filter by date, provider, location, payer, or type.">
        <div className="seg">
          <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List className="h-3.5 w-3.5 mr-1 inline" />List</button>
          <button className={viewMode === 'calendar' ? 'active' : ''} onClick={() => setViewMode('calendar')}><CalendarDays className="h-3.5 w-3.5 mr-1 inline" />Calendar</button>
        </div>
        <Button variant="outline"><Download className="h-4 w-4 mr-1.5" />Export</Button>
        <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => setAddOpen(true)}><CalendarPlus className="h-4 w-4 mr-1.5" />Add appointment</Button>
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9 bg-white h-9" placeholder="Search patient, plan, CPT…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="seg">{TIMEFRAMES.map((t) => <button key={t.k} className={timeframe === t.k ? 'active' : ''} onClick={() => setTimeframe(t.k)}>{t.label}</button>)}</div>
        <Select value={provider} onValueChange={setProvider}><SelectTrigger className={selCls}><SelectValue placeholder="Provider" /></SelectTrigger><SelectContent><SelectItem value="all">All providers</SelectItem>{providers.map((p) => <SelectItem key={p.id} value={p.id}>Dr. {p.lastName}</SelectItem>)}</SelectContent></Select>
        <Select value={location} onValueChange={setLocation}><SelectTrigger className={selCls}><SelectValue placeholder="Department" /></SelectTrigger><SelectContent><SelectItem value="all">All departments</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
        <Select value={payer} onValueChange={setPayer}><SelectTrigger className={selCls}><SelectValue placeholder="Payer" /></SelectTrigger><SelectContent><SelectItem value="all">All payers</SelectItem>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Select value={visitType} onValueChange={setVisitType}><SelectTrigger className={selCls}><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem>{VISIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <div className="inline-flex items-center gap-1.5">
          <Layers className="h-4 w-4 text-slate-400" />
          <span className="text-xs text-slate-500">Group</span>
          <div className="seg">
            {[['none', 'None'], ['type', 'Type'], ['date', 'Date']].map(([k, l]) => (
              <button key={k} className={groupBy === k ? 'active' : ''} onClick={() => setGroupBy(k)}>{l}</button>
            ))}
          </div>
        </div>
        {filtersActive && <Button variant="ghost" size="sm" className="text-slate-500" onClick={clear}>Clear</Button>}
        <span className="ml-auto text-xs text-slate-500 tnum">{rows.length} appointments</span>
      </div>

      {/* Table / Calendar */}
      {viewMode === 'calendar' ? (
        <AppointmentCalendar items={view} onSelect={(i) => setPeek(i)} initialDate={rows[0]?.apt?.startTime} />
      ) : (
      <div className="dh-card-flush overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50/70">
            <tr>{['When', ...(scopeAll ? ['Practice'] : []), 'Patient', 'Provider', 'Department', 'Type', 'Plan / payer', 'CPT', 'Risk', 'Status', ''].map((h, i) => <th key={i} className="text-left font-medium px-4 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(() => {
              let lastGroup = null;
              const keyOf = (w) => (groupBy === 'type' ? w.visitType : groupBy === 'date' ? dateBucket(w.apt.startTime) : null);
              return view.map((w, vIdx) => {
                const gkey = keyOf(w);
                const showHeader = groupBy !== 'none' && gkey !== lastGroup;
                lastGroup = gkey;
                const count = showHeader ? view.filter((x) => keyOf(x) === gkey).length : 0;
                return (
                  <React.Fragment key={w.id}>
                    {showHeader && (
                      <tr className="bg-slate-50/80 border-t border-slate-100">
                        <td colSpan={colSpan} className="px-4 py-2">
                          {groupBy === 'type'
                            ? <span className={`pill ${TYPE_CHIP[gkey] || 'pill-slate'}`}>{gkey}</span>
                            : <span className="text-sm font-semibold text-slate-700">{gkey}</span>}
                          <span className="ml-2 text-xs text-slate-500 tnum">{count} appointment{count === 1 ? '' : 's'}</span>
                        </td>
                      </tr>
                    )}
                    <tr className="border-t border-slate-100 row-hover cursor-pointer" onClick={() => setPeek(vIdx)}>
                      <td className="px-4 py-3"><div className="font-medium text-slate-900">{shortDate(w.apt.startTime)}</div><div className="text-xs text-slate-500 tnum">{time(w.apt.startTime)}</div></td>
                      {scopeAll && <td className="px-4 py-3 text-slate-600 text-xs">{w.practice?.name}</td>}
                      <td className="px-4 py-3"><div className="font-medium text-slate-900">{fullName(w.patient)}</div><div className="text-xs text-slate-500">{w.apt.reason}</div></td>
                      <td className="px-4 py-3 text-slate-700">Dr. {w.provider?.lastName}</td>
                      <td className="px-4 py-3 text-slate-600">{w.location?.name || '—'}</td>
                      <td className="px-4 py-3"><span className={`pill ${TYPE_CHIP[w.visitType] || 'pill-slate'}`}>{w.visitType}</span></td>
                      <td className="px-4 py-3"><div className="text-slate-900">{w.plan?.planName}</div><div className="text-xs text-slate-500">{w.payer?.name}</div></td>
                      <td className="px-4 py-3 mono text-xs text-slate-700">{w.apt.cpt}</td>
                      <td className="px-4 py-3"><RiskBadge level={w.risk.level} score={w.risk.score} showScore /></td>
                      <td className="px-4 py-3"><StatusPill status={w.agentState} /></td>
                      <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                    </tr>
                  </React.Fragment>
                );
              });
            })()}
          </tbody>
        </table>
        {rows.length === 0 && <EmptyState icon={CalendarDays} title="No appointments match" hint="Adjust the filters or clear them to see the full schedule." action={<Button variant="outline" onClick={clear}>Clear filters</Button>} />}
      </div>
      )}

      {peek !== null && view[peek] && (
        <PeekDrawer
          item={view[peek]}
          state={view[peek].agentState}
          index={peek}
          total={view.length}
          onPrev={() => setPeek((p) => Math.max(0, p - 1))}
          onNext={() => setPeek((p) => Math.min(view.length - 1, p + 1))}
          onClose={() => setPeek(null)}
          onApprove={() => { toast.success(`Approved — ${fullName(view[peek].patient)}`); setPeek(null); }}
          onEscalate={() => { toast.message('Escalated to manager'); setPeek(null); }}
        />
      )}

      <AddAppointment open={addOpen} onClose={() => setAddOpen(false)} practiceId={practiceId} />
    </div>
  );
}
